import type { PublisherAction, PublisherWithStats } from '@planuze/admin-contracts';
import { Button } from '@planuze/ui';
import { usePublishersContent } from '../../content/sections/publishers.js';

/* Ações de governança por linha (ADR-0471). Verificar segue direto (root-sign, ADR-0405);
   suspend/ban + reversos passam por confirmação com motivo no page (via onRequestAction).
   ban/unban só aparecem para super_admin (defesa em profundidade além do MANAGE_ROLES). */

/* Ações que mudam status (verify tem trilha própria via onVerify, nunca por onRequestAction). */
export type StatusAction = Exclude<PublisherAction, 'verify'>;

/* Um publisher precisa de verify se não está verified OU tem chave pendente de root-sign. */
function needsVerify(publisher: PublisherWithStats): boolean {
  return (
    publisher.status !== 'verified' || publisher.keys.some((key) => !key.rootSigned && !key.revoked)
  );
}

export function PublisherActions({
  publisher,
  canManage,
  isSuperAdmin,
  busy,
  onVerify,
  onRequestAction,
}: {
  readonly publisher: PublisherWithStats;
  readonly canManage: boolean;
  readonly isSuperAdmin: boolean;
  readonly busy: boolean;
  readonly onVerify: (id: string) => void;
  readonly onRequestAction: (publisher: PublisherWithStats, action: StatusAction) => void;
}) {
  const copy = usePublishersContent();
  if (!canManage) return null;
  const { status } = publisher;
  return (
    <div className="flex justify-end gap-2">
      {needsVerify(publisher) && (
        <Button size="sm" disabled={busy} onClick={() => onVerify(publisher.id)}>
          {copy.actionLabels.verify}
        </Button>
      )}
      {(status === 'verified' || status === 'pending') && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => onRequestAction(publisher, 'suspend')}
        >
          {copy.actionLabels.suspend}
        </Button>
      )}
      {status === 'suspended' && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => onRequestAction(publisher, 'unsuspend')}
        >
          {copy.actionLabels.unsuspend}
        </Button>
      )}
      {isSuperAdmin && status !== 'banned' && (
        <Button
          size="sm"
          variant="destructive"
          disabled={busy}
          onClick={() => onRequestAction(publisher, 'ban')}
        >
          {copy.actionLabels.ban}
        </Button>
      )}
      {isSuperAdmin && status === 'banned' && (
        <Button
          size="sm"
          variant="outline"
          disabled={busy}
          onClick={() => onRequestAction(publisher, 'unban')}
        >
          {copy.actionLabels.unban}
        </Button>
      )}
    </div>
  );
}
